import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { AdminPermissions } from "../../../utils/types";
import { AuthState } from "../../types";
import { getStoredUser } from "../../../utils/helpers/common";
import { API_URL, axiosObj } from "../../../config/apiClients";
import { ADMIN_STORAGE_KEYS } from "../../../config/storageKeys";

interface loginFormData {
  email: string;
  password: string;
  turnstileToken?: string;
}

interface UpdatePasswordFormData {
  adminId: string;
  currentPassword: string;
  newPassword: string;
}

const getStoredPermissions = (): AdminPermissions | undefined => {
  const stored = localStorage.getItem('user-permissions');
  return stored ? JSON.parse(stored) : undefined;
};

const initialState: AuthState = {
  isAuthenticated: Boolean(getStoredUser()),
  isLoading: false,
  user: getStoredUser(),
  permissions: getStoredPermissions()
};

export const loginUser = createAsyncThunk(
  "/auth/login",
  async (formData: loginFormData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, formData, { withCredentials: true });
      return response.data;
    } catch (error: any) {
      const responseData = error.response?.data;
      const errorMessage = responseData?.payload?.message ||
        responseData?.message ||
        error.message ||
        'Unable to log in due to a server issue. Please try again later.';
      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status,
        fullError: responseData
      });
    }
  }
);

export const updateAdminPassword = createAsyncThunk(
  "/auth/password/update",
  async (formData: UpdatePasswordFormData, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch(`${API_URL}/auth/password/update`, formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Update Admin Password' });
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    logOutUser: (state) => {
      state.isAuthenticated = false;
      state.user = undefined;
      state.permissions = undefined;
      localStorage.removeItem(ADMIN_STORAGE_KEYS.userInfo);
      localStorage.removeItem('user-permissions');
    },
    checkAuthStatus: (state) => {
      state.user = getStoredUser();
      state.permissions = getStoredPermissions();
      state.isAuthenticated = Boolean(state.user);
    }
  },
  extraReducers(builder) {
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = action.payload.status === "success";
        if (action.payload.payload?.updatedUser) {
          state.user = {
            _id: action.payload.payload.updatedUser._id,
            role: action.payload.payload.updatedUser.role,
            firstName: action.payload.payload.updatedUser.firstName,
            surname: action.payload.payload.updatedUser.surname,
          };
        }

        if (action.payload.payload?.adminPermissionsExists) {
          state.permissions = action.payload.payload.adminPermissionsExists;
        }
        if (state.isAuthenticated) {
          localStorage.setItem(ADMIN_STORAGE_KEYS.userInfo, JSON.stringify(state.user));
          if (state.permissions) {
            localStorage.setItem('user-permissions', JSON.stringify(state.permissions));
          }
        }
      })
      .addCase(loginUser.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = undefined;
        state.permissions = undefined;
      })
      .addCase(updateAdminPassword.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateAdminPassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateAdminPassword.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const { logOutUser, checkAuthStatus } = authSlice.actions;

export const logoutUser = createAsyncThunk("/auth/logout", async (_, { dispatch }) => {
  dispatch(logOutUser());
  await axiosObj.post("/auth/logout").catch(() => undefined);
});
export default authSlice.reducer;